import { useQuery } from '@tanstack/react-query';
import { Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { listLookup, LookupRecord } from '../../../api/masterData';
import { listUsers } from '../../../api/users';
import { useAuth } from '../../../context/authContext';
import { WizardValues } from './wizardTypes';
import { SearchableLookupField } from './SearchableLookupField';
import { isoToFlagEmoji } from './wizardUtils';

interface Step1Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
  leadNumber: string | null;
}

export const Step1PersonalDetails = ({ values, setField, leadNumber }: Step1Props) => {
  const { hasPermission } = useAuth();
  // Branch/counselor pickers hit /branches and /users, which (unlike master-data lookups)
  // require branches:view/users:view — permissions a plain Counselor doesn't hold. Leads
  // already default to the creator's own branch and stay unassigned server-side, matching
  // the pre-wizard LeadFormDialog's behavior, so these fields are simply omitted rather
  // than shown and perpetually failing to load for most of the people using this wizard.
  const canPickBranch = hasPermission('branches', 'view');
  const canAssignCounselor = hasPermission('leads', 'assign') && hasPermission('users', 'view');

  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });
  const branchesQuery = useQuery({
    queryKey: ['lookup', '/branches'],
    queryFn: () => listLookup('/branches', { activeOnly: true }),
    enabled: canPickBranch,
  });
  const sourcesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-sources'],
    queryFn: () => listLookup('/master-data/lead-sources', { activeOnly: true }),
  });
  const counselorsQuery = useQuery({
    queryKey: ['users-for-counselor'],
    queryFn: () => listUsers({ page: 1, pageSize: 100, isActive: true }),
    enabled: canAssignCounselor,
  });

  const flagFor = (option: LookupRecord) =>
    typeof option.iso2 === 'string' ? isoToFlagEmoji(option.iso2) : undefined;

  return (
    <Stack spacing={2}>
      {leadNumber && (
        <Chip
          label={`Lead number: ${leadNumber}`}
          color="primary"
          variant="outlined"
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="First name"
          fullWidth
          required
          value={values.firstName}
          onChange={(e) => setField('firstName', e.target.value)}
        />
        <TextField
          label="Surname"
          fullWidth
          required
          value={values.lastName}
          onChange={(e) => setField('lastName', e.target.value)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Gender"
          fullWidth
          value={values.gender}
          onChange={(e) => setField('gender', e.target.value as WizardValues['gender'])}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          <MenuItem value="MALE">Male</MenuItem>
          <MenuItem value="FEMALE">Female</MenuItem>
          <MenuItem value="OTHER">Other</MenuItem>
        </TextField>
        <TextField
          label="Date of birth"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={values.dateOfBirth}
          onChange={(e) => setField('dateOfBirth', e.target.value)}
        />
        <TextField
          label="NIC / Passport"
          fullWidth
          value={values.nicOrPassport}
          onChange={(e) => setField('nicOrPassport', e.target.value)}
        />
      </Stack>

      <SearchableLookupField
        label="Nationality"
        options={countriesQuery.data}
        value={values.nationalityCountryId}
        onChange={(id) => setField('nationalityCountryId', id)}
        getFlag={flagFor}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={values.email}
          onChange={(e) => setField('email', e.target.value)}
        />
        <TextField
          label="Mobile"
          fullWidth
          required
          value={values.phone}
          onChange={(e) => setField('phone', e.target.value)}
        />
        <TextField
          label="Telephone"
          fullWidth
          value={values.telephone}
          onChange={(e) => setField('telephone', e.target.value)}
        />
      </Stack>

      <TextField
        label="Address"
        fullWidth
        multiline
        minRows={2}
        value={values.address}
        onChange={(e) => setField('address', e.target.value)}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label="City" fullWidth value={values.city} onChange={(e) => setField('city', e.target.value)} />
        <TextField
          label="Postal code"
          fullWidth
          value={values.postalCode}
          onChange={(e) => setField('postalCode', e.target.value)}
        />
      </Stack>

      <Typography variant="subtitle2" sx={{ pt: 1 }}>
        Lead attribution
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {canPickBranch && (
          <TextField
            select
            label="Branch"
            fullWidth
            value={values.branchId}
            onChange={(e) => setField('branchId', e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Default (my branch)</em>
            </MenuItem>
            {branchesQuery.data?.map((b) => (
              <MenuItem key={b.id as number} value={b.id as number}>
                {String(b.name)}
              </MenuItem>
            ))}
          </TextField>
        )}
        {canAssignCounselor && (
          <TextField
            select
            label="Counselor"
            fullWidth
            value={values.assignedToId}
            onChange={(e) => setField('assignedToId', e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Unassigned</em>
            </MenuItem>
            {counselorsQuery.data?.data.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Lead source"
          fullWidth
          required
          value={values.leadSourceId}
          onChange={(e) => setField('leadSourceId', e.target.value ? Number(e.target.value) : '')}
        >
          {sourcesQuery.data?.map((s) => (
            <MenuItem key={s.id as number} value={s.id as number}>
              {String(s.name)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Stack>
  );
};
